import { Periods } from 'components/ProFormaReturnsTable/constant';
import { ProFormaGenericColumn, ProFormaGenericRow } from '../../ProFormaGenericTable/types';
import {
    IContributions,
    IDistributions,
    IDistributionWaterfall,
    IGpGpContributions,
    IGpGpDistributions,
    IGpGpDistributionWaterfall,
    IGpGpNetCashFlow,
    INetCashFlow,
    IProFormaPartner,
    IWaterfallSummary,
    IContributionsCashFlow,
    IProFormaGeneralInfo,
    IGpGpContributionsCashFlow,
    IDistributionWaterfallCashFlow,
    IGpGpDistributionWaterfallCashFlow,
    INetCashFlowCashFlow,
    IGpGpNetCashFlowCashFlow,
    IDistributionsCashFlow,
    IGpGpDistributionsCashFlow,
} from '../../../types';
import { getAmountPeriods, getDistributionPeriods } from './helpers';

export const OVERVIEW = 'Overview';
export const GP_EXPANDED = 'GP Detail';
export const LP_EXPANDED = 'LP Detail';

export const WATERFALL_TABS = [OVERVIEW, GP_EXPANDED, LP_EXPANDED];

export const WATERFALL_SUMMARY_COLUMNS: ProFormaGenericColumn[] = [
    {
        key: 'title', field: 'title', header: 'Summary', format: 'none', isReadOnly: true,
    },
    {
        key: 'contribution', field: 'contribution', header: 'Contributions', format: 'currency', isReadOnly: true,
    },
    {
        key: 'netProfit', field: 'netProfit', header: 'Net profit', format: 'currency', isReadOnly: true,
    },
    {
        key: 'roi', field: 'roi', header: 'ROI', format: 'percentage', isReadOnly: true,
    },
    {
        key: 'emx', field: 'emx', header: 'EMx', format: 'ratio', isReadOnly: true,
    },
    {
        key: 'irr', field: 'irr', header: 'IRR', format: 'percentage', isReadOnly: true,
    },
];

export const transformWaterfallSummaryRows = (isSingleGp?: boolean, waterfallSummary?: IWaterfallSummary): ProFormaGenericRow[] => {
    if (waterfallSummary === undefined) {
        return [];
    }

    return [
        {
            id: 'generalPartner',
            title: waterfallSummary.gp1Name,
            contribution: waterfallSummary.gp1Contributions,
            netProfit: waterfallSummary.gp1NetProfit,
            roi: waterfallSummary.gp1Roi ?? '0',
            emx: waterfallSummary.gp1Emx,
            irr: waterfallSummary.gp1Irr ?? '0',
        },
        ...(isSingleGp ? [] : [{
            id: 'generalPartner2',
            title: waterfallSummary.gp2Name,
            contribution: waterfallSummary.gp2Contributions,
            netProfit: waterfallSummary.gp2NetProfit,
            roi: waterfallSummary.gp2Roi ?? '0',
            emx: waterfallSummary.gp2Emx,
            irr: waterfallSummary.gp2Irr ?? '0',
        }]),
        {
            id: 'limitedPartner',
            title: waterfallSummary.lpName,
            contribution: waterfallSummary.lpContributions,
            netProfit: waterfallSummary.lpNetProfit,
            roi: waterfallSummary.lpRoi ?? '0',
            emx: waterfallSummary.lpEmx,
            irr: waterfallSummary.lpIrr ?? '0',
        },
    ];
};

export const transformContributionsRows = (
    gp?: IProFormaPartner,
    lp?: IProFormaPartner,
    contributions?: IContributions,
    contributionsCashFlow?: IContributionsCashFlow[],
    proForma?: IProFormaGeneralInfo,
    cashFlowPeriodsByCadence?: Record<Periods, string[]>,
    periodCadence?: Periods,
) => {
    if (contributions === undefined || gp === undefined || lp === undefined || !proForma || !cashFlowPeriodsByCadence || !periodCadence) {
        return [];
    }

    const periods = getAmountPeriods(proForma, cashFlowPeriodsByCadence, periodCadence, contributionsCashFlow || []);

    return [
        {
            id: gp.id?.toString() ?? '1',
            disableDelete: true,
            name: gp.name,
            share: gp.contributionShare,
            amount: contributions.gpAmount ?? '0',
            label: 'GP',
            ...Object.fromEntries(
                periods.map((period, index) => [
                    `column${index + 1}`,
                    period.values[`gp_${index}`],
                ]),
            ),
        },
        {
            id: lp.id?.toString() ?? '2',
            disableDelete: true,
            name: lp.name,
            share: lp.contributionShare,
            amount: contributions.lpAmount ?? '0',
            label: 'LP',
            ...Object.fromEntries(
                periods.map((period, index) => [
                    `column${index + 1}`,
                    period.values[`lp_${index}`],
                ]),
            ),
        },
    ];
};

export const transformGpContributionsRows = (
    gp?: IProFormaPartner,
    contributions?: IGpGpContributions,
    contributionsCashFlow?: IGpGpContributionsCashFlow[],
    proForma?: IProFormaGeneralInfo,
    cashFlowPeriodsByCadence?: Record<Periods, string[]>,
    periodCadence?: Periods,
) => {
    if (contributions === undefined || gp === undefined || !proForma || !cashFlowPeriodsByCadence || !periodCadence) {
        return [];
    }

    const periods = getAmountPeriods(proForma, cashFlowPeriodsByCadence, periodCadence, contributionsCashFlow || [], {
        gpField: 'gp1Amount',
        lpField: 'gp2Amount',
        totalField: 'totalAmount',
    });

    return gp.children
        ?.sort((a, b) => (a.id ?? 0) - (b.id ?? 0))
        .map((child, index) => ({
            id: child.id?.toString() ?? index.toString(),
            disableDelete: true,
            name: child.name,
            share: child.contributionShare,
            amount: index === 0 ? contributions.gp1EquityAmount ?? '0' : contributions.gp2EquityAmount ?? '0',
            label: `GP ${index + 1}`,
            ...Object.fromEntries(
                periods.map((period, periodIndex) => [
                    `column${periodIndex + 1}`,
                    index === 0 ? period.values[`gp_${periodIndex}`] : period.values[`lp_${periodIndex}`],
                ]),
            ),
        })) ?? [];
};

export const transformDistributionWaterfallRows = (
    distributionWaterfall?: IDistributionWaterfall,
    proForma?: IProFormaGeneralInfo,
    cashFlowPeriodsByCadence?: Record<Periods, string[]>,
    periodCadence?: Periods,
    distributionWaterfallCashFlow?: IDistributionWaterfallCashFlow[],
) => {
    if (distributionWaterfall === undefined || proForma === undefined || !cashFlowPeriodsByCadence || !periodCadence) {
        return [];
    }

    const periods = getDistributionPeriods(proForma, cashFlowPeriodsByCadence, periodCadence, distributionWaterfallCashFlow || []);

    return [{
        id: '0',
        name: 'Pref. return',
        gpCatchUp: distributionWaterfall?.gpCatchUp ?? 0,
        upTo: distributionWaterfall?.preferredReturnHurdleRate,
        gpPromote: String(distributionWaterfall?.prefReturnGpPromote),
        distToGP: distributionWaterfall?.prefReturnDistributionPercentToGp ?? '0',
        distToLP: distributionWaterfall?.prefReturnDistributionPercentToLp ?? '0',
        label: 'up to',
        ...Object.fromEntries(
            periods.map((period, index) => [
                `column${index + 1}`,
                period.values[`pref_${index}`],
            ]),
        ),
    },
    {
        id: '1',
        name: '2nd tier',
        upTo: distributionWaterfall?.secondTierHurdleRate,
        gpPromote: distributionWaterfall?.secondTierGpPromote,
        distToGP: distributionWaterfall?.tier2DistributionPercentToGp ?? '0',
        distToLP: distributionWaterfall?.tier2DistributionPercentToLp ?? '0',
        label: 'up to',
        ...Object.fromEntries(
            periods.map((period, index) => [
                `column${index + 1}`,
                period.values[`tier2_${index}`],
            ]),
        ),
    },
    {
        id: '2',
        name: '3rd tier',
        upTo: distributionWaterfall?.thirdTierHurdleRate,
        gpPromote: distributionWaterfall?.thirdTierGpPromote,
        distToGP: distributionWaterfall?.tier3DistributionPercentToGp ?? '0',
        distToLP: distributionWaterfall?.tier3DistributionPercentToLp ?? '0',
        label: 'up to',
        ...Object.fromEntries(
            periods.map((period, index) => [
                `column${index + 1}`,
                period.values[`tier3_${index}`] ?? '-',
            ]),
        ),
    },
    {
        id: '3',
        name: '4th tier',
        upTo: distributionWaterfall?.fourthTierHurdleRate,
        gpPromote: distributionWaterfall?.fourthTierGpPromote,
        distToGP: distributionWaterfall?.tier4DistributionPercentToGp ?? '0',
        distToLP: distributionWaterfall?.tier4DistributionPercentToLp ?? '0',
        label: 'above',
        ...Object.fromEntries(
            periods.map((period, index) => [
                `column${index + 1}`,
                period.values[`tier4_${index}`] ?? '-',
            ]),
        ),
    },
    ];
};

export const transformGpDistributionWaterfallRows = (
    distributionWaterfall?: IGpGpDistributionWaterfall,
    proForma?: IProFormaGeneralInfo,
    cashFlowPeriodsByCadence?: Record<Periods, string[]>,
    periodCadence?: Periods,
    distributionWaterfallCashFlow?: IGpGpDistributionWaterfallCashFlow[],
) => {
    if (distributionWaterfall === undefined || proForma === undefined || !cashFlowPeriodsByCadence || !periodCadence) {
        return [];
    }

    const periods = getDistributionPeriods(proForma, cashFlowPeriodsByCadence, periodCadence, distributionWaterfallCashFlow || []);

    return [{
        id: '0',
        name: 'Pref. return',
        upTo: distributionWaterfall?.preferredReturnHurdleRate,
        gpPromote: String(distributionWaterfall?.prefReturnGpPromote),
        distToGP: distributionWaterfall?.prefReturnDistributionPercentToGp1 ?? '0',
        distToLP: distributionWaterfall?.prefReturnDistributionPercentToGp2 ?? '0',
        label: 'up to',
        ...Object.fromEntries(
            periods.map((period, index) => [
                `column${index + 1}`,
                period.values[`pref_${index}`],
            ]),
        ),
    },
    {
        id: '1',
        name: '2nd tier',
        upTo: distributionWaterfall?.secondTierHurdleRate,
        gpPromote: distributionWaterfall?.secondTierGpPromote,
        distToGP: distributionWaterfall?.tier2DistributionPercentToGp1 ?? '0',
        distToLP: distributionWaterfall?.tier2DistributionPercentToGp2 ?? '0',
        label: 'up to',
        ...Object.fromEntries(
            periods.map((period, index) => [
                `column${index + 1}`,
                period.values[`tier2_${index}`],
            ]),
        ),
    },
    {
        id: '2',
        name: '3rd tier',
        upTo: distributionWaterfall?.thirdTierHurdleRate,
        gpPromote: distributionWaterfall?.thirdTierGpPromote,
        distToGP: distributionWaterfall?.tier3DistributionPercentToGp1 ?? '0',
        distToLP: distributionWaterfall?.tier3DistributionPercentToGp2 ?? '0',
        label: 'up to',
        ...Object.fromEntries(
            periods.map((period, index) => [
                `column${index + 1}`,
                period.values[`tier3_${index}`],
            ]),
        ),
    },
    {
        id: '3',
        name: '4th tier',
        upTo: distributionWaterfall?.fourthTierHurdleRate,
        gpPromote: distributionWaterfall?.fourthTierGpPromote,
        distToGP: distributionWaterfall?.tier4DistributionPercentToGp1 ?? '0',
        distToLP: distributionWaterfall?.tier4DistributionPercentToGp2 ?? '0',
        label: 'above',
        ...Object.fromEntries(
            periods.map((period, index) => [
                `column${index + 1}`,
                period.values[`tier4_${index}`],
            ]),
        ),
    },
    ];
};

export const transformDistributionRowsCashFlow = (
    distribution?: IDistributions,
    proForma?: IProFormaGeneralInfo,
    cashFlowPeriodsByCadence?: Record<Periods, string[]>,
    periodCadence?: Periods,
    distributionWaterfallCashFlow?: IDistributionsCashFlow[],
) => {
    if (distribution === undefined || proForma === undefined || !cashFlowPeriodsByCadence || !periodCadence) {
        return [];
    }

    const periods = getAmountPeriods(proForma, cashFlowPeriodsByCadence, periodCadence, distributionWaterfallCashFlow || []);

    return [
        {
            id: 'distribution-1',
            name: distribution?.gpName,
            share: distribution?.gpShare ?? '0',
            amount: distribution?.gpAmount,
            label: 'GP',
            isReadOnly: true,
            ...Object.fromEntries(
                periods.map((period, index) => [
                    `column${index + 1}`,
                    period.values[`gp_${index}`],
                ]),
            ),
        },
        {
            id: 'distribution-2',
            name: distribution?.lpName,
            share: distribution?.lpShare ?? '0',
            amount: distribution?.lpAmount,
            label: 'LP',
            isReadOnly: true,
            ...Object.fromEntries(
                periods.map((period, index) => [
                    `column${index + 1}`,
                    period.values[`lp_${index}`],
                ]),
            ),
        },
    ];
};

export const transformGpDistributionRowsCashFlow = (
    distribution?: IGpGpDistributions,
    proForma?: IProFormaGeneralInfo,
    cashFlowPeriodsByCadence?: Record<Periods, string[]>,
    periodCadence?: Periods,
    distributionWaterfallCashFlow?: IGpGpDistributionsCashFlow[],
) => {
    if (distribution === undefined || proForma === undefined || !cashFlowPeriodsByCadence || !periodCadence) {
        return [];
    }

    const periods = getAmountPeriods(proForma, cashFlowPeriodsByCadence, periodCadence, distributionWaterfallCashFlow || [], {
        gpField: 'gp1Amount',
        lpField: 'gp2Amount',
        totalField: 'totalAmount',
    });

    return [
        {
            id: 'distribution-1',
            name: distribution?.gp1Name,
            share: distribution?.gp1Share ?? '0',
            amount: distribution?.gp1Amount,
            label: 'GP 1',
            isReadOnly: true,
            ...Object.fromEntries(
                periods.map((period, periodIndex) => [
                    `column${periodIndex + 1}`,
                    period.values[`gp_${periodIndex}`],
                ]),
            ),
        },
        {
            id: 'distribution-2',
            name: distribution?.gp2Name,
            share: distribution?.gp2Share ?? '0',
            amount: distribution?.gp2Amount,
            label: 'GP 2',
            isReadOnly: true,
            ...Object.fromEntries(
                periods.map((period, periodIndex) => [
                    `column${periodIndex + 1}`,
                    period.values[`lp_${periodIndex}`],
                ]),
            ),
        },
    ];
};

export const transformNetCashFlowsRows = (
    netCashFlow?: INetCashFlow,
    proForma?: IProFormaGeneralInfo,
    cashFlowPeriodsByCadence?: Record<Periods, string[]>,
    periodCadence?: Periods,
    netCashFlowCashFlow?: INetCashFlowCashFlow[],
) => {
    if (netCashFlow === undefined || proForma === undefined || !cashFlowPeriodsByCadence || !periodCadence) {
        return [];
    }

    const periods = getAmountPeriods(proForma, cashFlowPeriodsByCadence, periodCadence, netCashFlowCashFlow || []);

    return [
        {
            id: 'cash-flow-1',
            name: netCashFlow?.gpName,
            emx: netCashFlow?.gpEmx?.toString() ?? '0',
            irr: netCashFlow?.gpIrr ?? '0',
            share: netCashFlow?.gpShare ?? '0',
            amount: netCashFlow?.gpAmount,
            label: 'GP',
            isReadOnly: true,
            ...Object.fromEntries(
                periods.map((period, index) => [
                    `column${index + 1}`,
                    period.values[`gp_${index}`],
                ]),
            ),
        },
        {
            id: 'cash-flow-2',
            name: netCashFlow?.lpName,
            emx: netCashFlow?.lpEmx?.toString() ?? '0',
            irr: netCashFlow?.lpIrr ?? '0',
            share: netCashFlow?.lpShare ?? '0',
            amount: netCashFlow?.lpAmount?.toString(),
            label: 'LP',
            isReadOnly: true,
            ...Object.fromEntries(
                periods.map((period, index) => [
                    `column${index + 1}`,
                    period.values[`lp_${index}`],
                ]),
            ),
        },
    ];
};

export const transformGpNetCashFlowsRows = (
    netCashFlow?: IGpGpNetCashFlow,
    proForma?: IProFormaGeneralInfo,
    cashFlowPeriodsByCadence?: Record<Periods, string[]>,
    periodCadence?: Periods,
    netCashFlowCashFlow?: IGpGpNetCashFlowCashFlow[],
) => {
    if (netCashFlow === undefined || proForma === undefined || !cashFlowPeriodsByCadence || !periodCadence) {
        return [];
    }

    const periods = getAmountPeriods(proForma, cashFlowPeriodsByCadence, periodCadence, netCashFlowCashFlow || [], {
        gpField: 'gp1Amount',
        lpField: 'gp2Amount',
        totalField: 'totalAmount',
    });

    return [
        {
            id: 'cash-flow-1',
            name: netCashFlow?.gp1Name,
            emx: netCashFlow?.gp1Emx?.toString() ?? '0',
            irr: netCashFlow?.gp1Irr ?? '0',
            share: netCashFlow?.gp1Share ?? '0',
            amount: netCashFlow?.gp1Amount?.toString(),
            label: 'GP 1',
            isReadOnly: true,
            ...Object.fromEntries(
                periods.map((period, periodIndex) => [
                    `column${periodIndex + 1}`,
                    period.values[`gp_${periodIndex}`],
                ]),
            ),
        },
        {
            id: 'cash-flow-2',
            name: netCashFlow?.gp2Name,
            emx: netCashFlow?.gp2Emx?.toString() ?? '0',
            irr: netCashFlow?.gp2Irr ?? '0',
            share: netCashFlow?.gp2Share ?? '0',
            amount: netCashFlow?.gp2Amount?.toString(),
            label: 'GP 2',
            isReadOnly: true,
            ...Object.fromEntries(
                periods.map((period, periodIndex) => [
                    `column${periodIndex + 1}`,
                    period.values[`lp_${periodIndex}`],
                ]),
            ),
        },
    ];
};
